import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../components/ui/Button';
import Card from '../../../../components/ui/Card';
import { PET_BALANCE } from './config/petBalance';
import LayeredPet from './LayeredPet';
import { useVirtualPet } from './hooks/useVirtualPet';
import { DungeonDifficulty } from './types';
import { ORIGINAL_PET_ASSETS } from './config/evolutionAssets';

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '20px',
  fontWeight: 600,
  color: 'var(--Color-text-strong, #0D0D0D)'
};

const badgeStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 600,
  background: 'var(--Color-fill-brand-color, #FF6F0F)',
  color: '#fff'
};

const SIDE_PANEL_WIDTH = 300;

type SidePanelMode = 'idle' | 'snack' | 'play';
type HubTab = 'growth' | 'character' | 'adventure' | 'friends';

const hubTabs: Array<{ id: HubTab; label: string; emoji: string }> = [
  { id: 'growth', label: '성장', emoji: '🌱' },
  { id: 'character', label: '캐릭터', emoji: '🥚' },
  { id: 'adventure', label: '모험', emoji: '🗺️' },
  { id: 'friends', label: '친구', emoji: '🤝' }
];

const difficultyLabels: Record<DungeonDifficulty, string> = {
  easy: '산책길',
  normal: '숲속 탐험',
  hard: '별빛 동굴'
};

const sidePanelTitle: Record<Exclude<SidePanelMode, 'idle'>, string> = {
  snack: '간식 주기',
  play: '놀아주기'
};

const PetRoomBackdrop: React.FC<{ isMobile: boolean }> = ({ isMobile }) => (
  <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    {/* 은은한 벽 패턴 */}
    <div
      style={{
        position: 'absolute',
        inset: 0,
        backgroundImage:
          'radial-gradient(circle at 18% 18%, rgba(255,255,255,.78) 0 2px, transparent 3px), radial-gradient(circle at 74% 28%, rgba(255,255,255,.55) 0 1.5px, transparent 2.5px)',
        backgroundSize: '92px 92px, 128px 128px',
        opacity: 0.7
      }}
    />

    {/* 별이 보이는 둥근 창문 */}
    <div
      style={{
        position: 'absolute',
        left: isMobile ? '8%' : '10%',
        top: isMobile ? 78 : 62,
        width: isMobile ? 126 : 180,
        height: isMobile ? 108 : 148,
        border: '8px solid rgba(255,255,255,.88)',
        borderRadius: '48% 48% 22px 22px',
        background: 'linear-gradient(180deg, #7776B9 0%, #A69DDE 52%, #F3B7D2 100%)',
        boxShadow: '0 10px 30px rgba(79,70,125,.18), inset 0 0 20px rgba(255,255,255,.3)'
      }}
    >
      <span style={{ position: 'absolute', top: '18%', left: '22%', color: '#FFF2A6', fontSize: 15 }}>✦</span>
      <span style={{ position: 'absolute', top: '35%', right: '18%', color: '#fff', fontSize: 10 }}>✦</span>
      <span style={{ position: 'absolute', top: '12%', right: '34%', color: '#fff', fontSize: 7 }}>●</span>
      <div style={{ position: 'absolute', right: '12%', bottom: '14%', width: '42%', height: '22%', borderRadius: 999, background: 'rgba(255,255,255,.72)', filter: 'blur(.2px)' }} />
      <div style={{ position: 'absolute', left: '24%', top: 0, width: 7, height: '100%', background: 'rgba(255,255,255,.72)' }} />
      <div style={{ position: 'absolute', left: '68%', top: 0, width: 7, height: '100%', background: 'rgba(255,255,255,.72)' }} />
    </div>

    {/* 벽 선반과 작은 장식 */}
    {!isMobile && (
      <div style={{ position: 'absolute', left: '39%', top: 100, width: 170, height: 12, borderRadius: 999, background: '#B8A7D8', boxShadow: '0 7px 13px rgba(73,58,100,.16)' }}>
        <span style={{ position: 'absolute', left: 18, bottom: 9, fontSize: 36 }}>🌙</span>
        <span style={{ position: 'absolute', left: 72, bottom: 9, fontSize: 32 }}>📘</span>
        <span style={{ position: 'absolute', right: 17, bottom: 8, fontSize: 30 }}>🪴</span>
      </div>
    )}

    {/* 바닥과 중앙 러그 */}
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: '34%',
        background: 'linear-gradient(180deg, #D8C8E8 0%, #C7B3DE 100%)',
        borderTop: '3px solid rgba(255,255,255,.55)'
      }}
    />
    <div
      style={{
        position: 'absolute',
        left: isMobile ? '13%' : '18%',
        bottom: isMobile ? '8%' : '7%',
        width: isMobile ? '72%' : '54%',
        height: isMobile ? '19%' : '23%',
        borderRadius: '50%',
        background: 'radial-gradient(ellipse, #FFF5D6 0 34%, #F7DCA9 35% 52%, #DCA9C7 53% 68%, #A896CF 69% 100%)',
        boxShadow: '0 12px 22px rgba(72,52,101,.17)',
        opacity: 0.92
      }}
    />

    {/* 전경의 몽글한 빛 */}
    <div style={{ position: 'absolute', width: 240, height: 240, right: '14%', top: '18%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,244,193,.28), transparent 68%)' }} />
  </div>
);

const VirtualPetHub: React.FC = () => {
  const navigate = useNavigate();
  const {
    loading,
    message,
    profile,
    stats,
    inventory,
    evolution,
    studentPoints,
    shares,
    dungeonRuns,
    ownedPetConsumables,
    playOptions,
    playActionStatus,
    dailyInteractionStatus,
    isEating,
    isPlaying,
    isSleeping,
    isEvolving,
    evolutionStatus,
    classSettings,
    actions
  } = useVirtualPet();

  const [sidePanelMode, setSidePanelMode] = useState<SidePanelMode>('idle');
  const [isStrokeMode, setIsStrokeMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeHubTab, setActiveHubTab] = useState<HubTab>('growth');

  useEffect(() => {
    if (sidePanelMode !== 'play') {
      setIsStrokeMode(false);
    }
  }, [sidePanelMode]);

  useEffect(() => {
    if (isStrokeMode && !playActionStatus.stroke.canPlay) {
      setIsStrokeMode(false);
    }
  }, [isStrokeMode, playActionStatus.stroke.canPlay]);

  useEffect(() => {
    if (!isStrokeMode) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsStrokeMode(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isStrokeMode]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => setIsMobile(e.matches);
    handleChange(mediaQuery);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // 캐릭터가 방 안을 자유롭게 돌아다니는 위치(% 기준)와 바라보는 방향
  const posRef = useRef({ x: 50, y: 62 });
  const [petPos, setPetPos] = useState(posRef.current);
  const [facingLeft, setFacingLeft] = useState(false);

  useEffect(() => {
    // 잠자는 중에는 가만히 있기
    if (isSleeping || isEvolving) return;
    let timer: ReturnType<typeof setTimeout>;
    const roam = () => {
      const isEgg = profile?.petForm === 'starEgg';
      const nextX = isEgg
        ? 42 + Math.random() * 16 // 별알: 중앙 42~58%
        : 30 + Math.random() * 40; // 성장형: 중앙 30~70%
      const nextY = isEgg
        ? 59 + Math.random() * 9 // 별알: 59~68%
        : 50 + Math.random() * 20; // 성장형: 50~70%
      setFacingLeft(nextX < posRef.current.x);
      posRef.current = { x: nextX, y: nextY };
      setPetPos(posRef.current);
      timer = setTimeout(roam, 3200 + Math.random() * 2400);
    };
    timer = setTimeout(roam, 700);
    return () => clearTimeout(timer);
  }, [isSleeping, isEvolving, profile?.petForm]);

  const groupedPetConsumables = useMemo(() => {
    const groups = new Map<string, { itemId: string; name: string; image?: string; count: number }>();
    ownedPetConsumables.forEach((item) => {
      const existing = groups.get(item.itemId);
      if (existing) {
        existing.count += 1;
      } else {
        groups.set(item.itemId, {
          itemId: item.itemId,
          name: item.name,
          image: item.image,
          count: 1
        });
      }
    });
    return Array.from(groups.values());
  }, [ownedPetConsumables]);

  const useGroupedConsumable = (itemId: string) => {
    const target = ownedPetConsumables.find((item) => item.itemId === itemId);
    if (target) actions.useConsumable(target.uniqueKey);
  };

  const handleOpenSnackPicker = () => {
    setSidePanelMode((prev) => (prev === 'snack' ? 'idle' : 'snack'));
  };

  const handleOpenPlayPicker = () => {
    setSidePanelMode((prev) => (prev === 'play' ? 'idle' : 'play'));
  };

  const closeSidePanel = () => setSidePanelMode('idle');

  const handleStrokeModeToggle = () => {
    if (!playActionStatus.stroke.canPlay) return;
    setIsStrokeMode((prev) => !prev);
  };

  const handlePetStroke = () => {
    if (!isStrokeMode) return;
    void actions.performPlayAction('stroke');
  };

  const expMax = profile ? profile.level * PET_BALANCE.levelExpMultiplier : 0;
  const expRatio = expMax > 0 && profile ? Math.min(100, Math.round((profile.exp / expMax) * 100)) : 0;
  const canEvolve = Boolean(evolutionStatus?.satisfied);
  const currentPetAsset = ORIGINAL_PET_ASSETS[profile?.petForm || 'starEgg'];

  if (loading) {
    return (
      <div style={{ padding: '24px', fontSize: '15px', color: 'var(--Color-text-normal, #161616)' }}>
        캐릭터 허브를 불러오는 중...
      </div>
    );
  }

  if (!classSettings.enabled) {
    return (
      <div style={{ maxWidth: 720, margin: '40px auto', padding: 20 }}>
        <Card>
          <div style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🌙</div>
            <h2 style={sectionTitleStyle}>가상펫이 잠시 쉬고 있어요</h2>
            <p style={{ color: 'var(--Color-text-normal, #666)', lineHeight: 1.6 }}>
              선생님이 학급 가상펫 운영을 다시 켜면 이용할 수 있어요.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (!profile || !stats || !inventory || !evolution) {
    return (
      <Card padding="24px" borderRadius="16px">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong, #0D0D0D)' }}>
            캐릭터 허브를 열 수 없어요
          </div>
          <div style={{ fontSize: '14px', color: 'var(--Color-text-normal, #161616)' }}>
            {message || '캐릭터 정보를 불러오지 못했어요.'}
          </div>
          <Button variant="primary" size="small" onClick={() => actions.refresh()}>
            다시 시도
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 풀스크린 무대 */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          minHeight: 'calc(100vh - 200px)',
          borderRadius: 20,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #DCDCF4 0%, #E8E2F6 48%, #DED2EC 66%, #C8B4DE 100%)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <PetRoomBackdrop isMobile={isMobile} />

        {/* 상단 좌측: 이름·Lv·진화 */}
        <div
          style={{
            position: 'absolute',
            top: 20,
            left: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            maxWidth: 'calc(100% - 120px)',
            padding: '8px 14px',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.72)',
            backdropFilter: 'blur(6px)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
            zIndex: 2
          }}
        >
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--Color-text-strong, #0D0D0D)' }}>
            {profile.name}
          </h1>
          <span style={badgeStyle}>Lv.{profile.level}</span>
          <span style={{ ...badgeStyle, background: '#0D0D0D' }}>{currentPetAsset.name}</span>
        </div>

        {/* 상단 우측: 요약 칩 */}
        <div
          style={{
            position: 'absolute',
            top: 20,
            right: isMobile ? 20 : SIDE_PANEL_WIDTH + 24,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            zIndex: 2
          }}
        >
          {[
            { label: 'P', value: `${studentPoints}` },
            { label: '♥', value: `${profile.intimacy}%` },
            { label: '던전', value: `${evolution.dungeonClearCount}회` }
          ].map((chip) => (
            <span
              key={chip.label}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '6px 12px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.72)',
                backdropFilter: 'blur(6px)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--Color-text-strong, #0D0D0D)'
              }}
            >
              <span style={{ opacity: 0.55 }}>{chip.label}</span>
              {chip.value}
            </span>
          ))}
        </div>

        {/* 본문 가로 2분할: 왼쪽(펫+상태창) | 오른쪽(활동) */}
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: isMobile ? 'column' : 'row',
            minHeight: 0,
            overflow: 'hidden'
          }}
        >
          {/* 왼쪽: 펫 공간 + 하단 상태창 */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div
              style={{
                position: 'relative',
                flex: 1,
                minHeight: isMobile ? 240 : 280,
                overflow: 'hidden',
                cursor: isStrokeMode ? 'pointer' : undefined
              }}
            >
              {isStrokeMode && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '8px 14px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    zIndex: 2,
                    pointerEvents: 'none'
                  }}
                >
                  캐릭터를 눌러 쓰다듬어 주세요
                </div>
              )}

              <div
                style={{
                  position: 'absolute',
                  left: `${petPos.x}%`,
                  top: `${petPos.y}%`,
                  transform: 'translate(-50%, -100%)',
                  transition: 'left 2.8s ease-in-out, top 2.8s ease-in-out',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  pointerEvents: isStrokeMode ? 'auto' : 'none',
                  cursor: isStrokeMode ? 'pointer' : undefined,
                  zIndex: isStrokeMode ? 3 : 1
                }}
                onClick={handlePetStroke}
                onKeyDown={(e) => {
                  if (isStrokeMode && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handlePetStroke();
                  }
                }}
                role={isStrokeMode ? 'button' : undefined}
                tabIndex={isStrokeMode ? 0 : undefined}
                aria-label={isStrokeMode ? `${profile.name} 쓰다듬기` : undefined}
              >
                <div
                  style={{
                    transform: facingLeft ? 'scaleX(-1)' : 'none',
                    transformOrigin: 'bottom center',
                    transition: 'transform 0.25s ease',
                    lineHeight: 0
                  }}
                >
                  <LayeredPet
                    size={isMobile ? 150 : 210}
                    equipped={inventory.equipped}
                    petForm={profile.petForm}
                    petName={profile.name}
                    isEating={isEating}
                    isPlaying={isPlaying}
                    isSleeping={isSleeping}
                    isEvolving={isEvolving}
                    isReadyToEvolve={canEvolve}
                  />
                </div>
                <div
                  style={{
                    width: 56,
                    height: 14,
                    marginTop: -2,
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.16)',
                    filter: 'blur(3px)'
                  }}
                />
              </div>

              {message && (
                <div
                  style={{
                    position: 'absolute',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '8px 16px',
                    borderRadius: 999,
                    background: 'rgba(13,13,13,0.78)',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    zIndex: 2,
                    maxWidth: '90%',
                    textAlign: 'center'
                  }}
                >
                  {message}
                </div>
              )}
            </div>

            {/* 하단 상태창 */}
            <div
              style={{
                margin: 16,
                marginRight: isMobile ? 16 : 8,
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(8px)',
                borderRadius: 16,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                flexShrink: 0
              }}
            >
          {/* EXP */}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                marginBottom: 4,
                color: 'var(--Color-text-normal, #161616)'
              }}
            >
              <span>EXP · 기분 {stats.mood}</span>
              <span>{profile.exp} / {expMax}</span>
            </div>
            <div
              style={{
                width: '100%',
                height: 8,
                borderRadius: 999,
                background: 'var(--Color-line-normal, #F0F0F0)',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  width: `${expRatio}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #FFD25A, var(--Color-fill-brand-color, #FF6F0F))',
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
          </div>

          {/* 상태 4분할 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 10
            }}
          >
            {[
              { label: '🍙', name: '배고픔', value: 100 - stats.hunger, color: '#FF8A65' },
              { label: '😊', name: '행복', value: stats.happiness, color: '#FFB74D' },
              { label: '⚡', name: '에너지', value: stats.energy, color: '#64B5F6' },
              { label: '❤️', name: '건강', value: stats.health, color: '#81C784' }
            ].map((s) => (
              <div key={s.name} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    color: 'var(--Color-text-normal, #161616)'
                  }}
                >
                  <span>{s.label} {s.name}</span>
                  <span>{Math.round(s.value)}%</span>
                </div>
                <div
                  style={{
                    height: 6,
                    borderRadius: 999,
                    background: 'var(--Color-line-normal, #F0F0F0)',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      width: `${Math.max(0, Math.min(100, s.value))}%`,
                      height: '100%',
                      background: s.color,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* 액션 */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              variant="primary"
              size="small"
              onClick={() => actions.interact('chat')}
              disabled={dailyInteractionStatus.chat <= 0}
            >
              대화 {dailyInteractionStatus.chat}/{classSettings.freeChatPerDay}
            </Button>
            <Button
              variant={sidePanelMode === 'snack' ? 'outline' : 'primary'}
              size="small"
              onClick={handleOpenSnackPicker}
            >
              간식 주기
            </Button>
            <Button
              variant={sidePanelMode === 'play' ? 'outline' : 'primary'}
              size="small"
              onClick={handleOpenPlayPicker}
            >
              놀아주기
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={() => actions.interact('rest')}
              disabled={dailyInteractionStatus.rest <= 0}
            >
              휴식 {dailyInteractionStatus.rest}/{classSettings.freeRestPerDay}
            </Button>
          </div>
            </div>
          </div>

          {/* 오른쪽: 간식 / 놀아주기 공간 (펫+상태창 전체 높이) */}
          <div
            style={{
              width: isMobile ? '100%' : SIDE_PANEL_WIDTH,
              flexShrink: 0,
              borderLeft: isMobile ? 'none' : '1px solid rgba(255,255,255,0.45)',
              borderTop: isMobile ? '1px solid rgba(255,255,255,0.45)' : 'none',
              background: 'rgba(255,255,255,0.55)',
              backdropFilter: 'blur(8px)',
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              overflowY: 'auto',
              minHeight: isMobile ? 200 : undefined
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h2 style={{ ...sectionTitleStyle, fontSize: 17, margin: 0 }}>
                {sidePanelMode === 'idle' ? '활동' : sidePanelTitle[sidePanelMode]}
              </h2>
              {sidePanelMode !== 'idle' && (
                <button
                  type="button"
                  onClick={closeSidePanel}
                  aria-label="닫기"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'rgba(0,0,0,0.06)',
                    fontSize: 16,
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                >
                  ×
                </button>
              )}
            </div>

            {sidePanelMode === 'snack' ? (
              groupedPetConsumables.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                  {groupedPetConsumables.map((item) => (
                    <button
                      key={item.itemId}
                      type="button"
                      onClick={() => useGroupedConsumable(item.itemId)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 12,
                        border: '1px solid var(--Color-line-normal, #F0F0F0)',
                        background: 'var(--Color-background-normal, white)',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      {item.image ? (
                        <img
                          src={item.image}
                          alt=""
                          style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 8, flexShrink: 0 }}
                        />
                      ) : (
                        <span style={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>🍪</span>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--Color-text-strong, #0D0D0D)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.name}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--Color-text-normal, #161616)' }}>
                          보유 {item.count}개
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--Color-fill-brand-color, #FF6F0F)',
                          flexShrink: 0
                        }}
                      >
                        사용
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, justifyContent: 'center' }}>
                  <div style={{ fontSize: 13, color: 'var(--Color-text-normal, #161616)', lineHeight: 1.5 }}>
                    보유한 간식이 없어요.
                    <br />
                    학급 상점에서 구매해 주세요.
                  </div>
                  <Button variant="primary" size="small" onClick={() => navigate('/class/student/ClassItemShop?store=pet')}>
                    학급 상점으로 이동
                  </Button>
                </div>
              )
            ) : sidePanelMode === 'play' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                {playOptions.map((option) => {
                  const status = playActionStatus[option.id];
                  const isStroke = option.id === 'stroke';
                  const disabled = !status.canPlay;
                  const strokeSelected = isStroke && isStrokeMode;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (isStroke) {
                          handleStrokeModeToggle();
                        } else {
                          void actions.performPlayAction(option.id);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 12,
                        border: strokeSelected
                          ? '2px solid var(--Color-fill-brand-color, #FF6F0F)'
                          : '1px solid var(--Color-line-normal, #F0F0F0)',
                        background: strokeSelected
                          ? 'rgba(255, 111, 15, 0.08)'
                          : 'var(--Color-background-normal, white)',
                        cursor: disabled ? 'not-allowed' : isStroke ? 'pointer' : 'pointer',
                        opacity: disabled ? 0.55 : 1,
                        textAlign: 'left'
                      }}
                    >
                      <span style={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>{option.emoji}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--Color-text-strong, #0D0D0D)'
                          }}
                        >
                          {option.name}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--Color-text-normal, #161616)', lineHeight: 1.4 }}>
                          {option.description}
                          <br />
                          {isStroke ? (
                            <>
                              무료 {status.freeRemaining ?? 0}/{classSettings.freeStrokePerDay}회
                              {status.ticketCount > 0 && ` · 이용권 ${status.ticketCount}개`}
                              {strokeSelected && (
                                <>
                                  <br />
                                  캐릭터를 눌러 쓰다듬어 주세요
                                </>
                              )}
                            </>
                          ) : status.ticketCount > 0 ? (
                            `이용권 ${status.ticketCount}개`
                          ) : (
                            '이용권 없음 · 학급 상점에서 구매'
                          )}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--Color-fill-brand-color, #FF6F0F)',
                          flexShrink: 0
                        }}
                      >
                        {isStroke ? (strokeSelected ? '선택됨' : '선택') : '하기'}
                      </span>
                    </button>
                  );
                })}
                <Button variant="outline" size="small" onClick={() => navigate('/class/student/ClassItemShop?store=pet')}>
                  놀아주기 이용권 구매하기
                </Button>
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '24px 8px',
                  textAlign: 'center',
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: 13,
                  lineHeight: 1.5
                }}
              >
                <span style={{ fontSize: 32 }}>✨</span>
                <span>
                  왼쪽 <strong>간식 주기</strong> 또는 <strong>놀아주기</strong>를 누르면
                  <br />
                  여기에서 활동을 선택할 수 있어요
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ width: '100%', maxWidth: 1180, margin: '24px auto 0', padding: '0 4px 32px' }}>
        <Card padding="12px" borderRadius="16px">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {hubTabs.map((tab) => (
              <Button
                key={tab.id}
                variant={activeHubTab === tab.id ? 'primary' : 'outline'}
                size="small"
                onClick={() => setActiveHubTab(tab.id)}
              >
                {tab.emoji} {tab.label}
              </Button>
            ))}
          </div>
        </Card>

        {activeHubTab === 'growth' && (
          <Card padding="20px" borderRadius="16px" style={{ marginTop: 12 }}>
            <h2 style={sectionTitleStyle}>성장 기록</h2>
            <div style={{ marginTop: 14, fontSize: 14 }}>
              레벨 {profile.level} · 경험치 {profile.exp}/{expMax} · 친밀도 {profile.intimacy}%
            </div>
            <div style={{ height: 10, marginTop: 8, borderRadius: 999, overflow: 'hidden', background: '#F0F0F0' }}>
              <div style={{ width: `${expRatio}%`, height: '100%', background: 'var(--Color-fill-brand-color, #FF6F0F)' }} />
            </div>
            <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
              <div style={{ padding: 14, borderRadius: 12, background: '#FFF7F0', fontSize: 13, lineHeight: 1.6 }}>
                <strong>다음 성장</strong><br />
                다음 모습: {evolutionStatus?.nextForm ? ORIGINAL_PET_ASSETS[evolutionStatus.nextForm].name : '최종 진화 완료'}
                <br />레벨 {evolutionStatus?.requiredLevel} · 친밀도 {evolutionStatus?.requiredIntimacy}%
                {Boolean(evolutionStatus?.requiredDungeonClearCount) && ` · 모험 ${evolutionStatus?.requiredDungeonClearCount}회`}
              </div>
              <div style={{ padding: 14, borderRadius: 12, background: '#F4F6FA', fontSize: 13, lineHeight: 1.6 }}>
                <strong>성장 성향</strong><br />
                교감 {profile.nurtureTraits.bond} · 돌봄 {profile.nurtureTraits.care} · 활동 {profile.nurtureTraits.courage}
                <br />현재 성향: {evolutionStatus?.dominantBranch === 'cloud' ? '구름' : evolutionStatus?.dominantBranch === 'flame' ? '불꽃' : '별'}
              </div>
              <div style={{ padding: 14, borderRadius: 12, background: '#F7F4FF', fontSize: 13, lineHeight: 1.7 }}>
                <strong>펫 능력치</strong><br />
                ✨ 별빛 {profile.abilities.starlight.toFixed(1)} · 🌿 생명력 {profile.abilities.vitality.toFixed(1)} · 🔥 용기 {profile.abilities.courage.toFixed(1)}
                <br /><span style={{ color: '#6B7280' }}>활동할 때마다 0.1~0.2씩 천천히 성장해요.</span>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <Button variant="primary" onClick={actions.evolve} disabled={!canEvolve || isEvolving}>
                {isEvolving ? '부화 중...' : evolutionStatus?.nextForm ? '진화하기' : '최종 진화 완료'}
              </Button>
            </div>
          </Card>
        )}

        {activeHubTab === 'character' && (
          <Card padding="20px" borderRadius="16px" style={{ marginTop: 12 }}>
            <h2 style={sectionTitleStyle}>내 캐릭터</h2>
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 14 }}>
              <img src={currentPetAsset.image} alt={currentPetAsset.name} style={{ width: 96, height: 96, objectFit: 'contain' }} />
              <div>
                <strong>{currentPetAsset.name}</strong>
                <div style={{ marginTop: 4, fontSize: 13, color: '#666' }}>선택하는 동물이 아니라 돌본 방식에 따라 모습이 달라져요.</div>
              </div>
            </div>
            <div style={{ marginTop: 20 }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>발견할 수 있는 성장 계보</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                {[
                  ['starBean', 'starKeeper'],
                  ['cloudBean', 'cloudGuardian'],
                  ['flameBean', 'flameDragon']
                ].map(([middle, final]) => (
                  <div key={middle} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, border: '1px solid #F0F0F0', borderRadius: 12 }}>
                    <img src={ORIGINAL_PET_ASSETS[middle as keyof typeof ORIGINAL_PET_ASSETS].image} alt="" style={{ width: 48, height: 48, objectFit: 'contain', imageRendering: 'pixelated' }} />
                    <div style={{ fontSize: 13 }}>
                      <strong>{ORIGINAL_PET_ASSETS[middle as keyof typeof ORIGINAL_PET_ASSETS].name}</strong>
                      <span style={{ margin: '0 5px', color: '#999' }}>→</span>
                      {ORIGINAL_PET_ASSETS[final as keyof typeof ORIGINAL_PET_ASSETS].name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {activeHubTab === 'adventure' && (
          <Card padding="20px" borderRadius="16px" style={{ marginTop: 12 }}>
            <h2 style={sectionTitleStyle}>모험 보내기</h2>
            <p style={{ margin: '8px 0 14px', fontSize: 13, color: '#666' }}>에너지를 사용해 모험을 완료하면 포인트와 경험치를 받아요.</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(['easy', 'normal', 'hard'] as DungeonDifficulty[]).map((difficulty) => (
                <Button key={difficulty} variant="primary" onClick={() => actions.startDungeon(difficulty)}>
                  {difficultyLabels[difficulty]} · 에너지 {PET_BALANCE.dungeonEnergy[difficulty]}
                </Button>
              ))}
            </div>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dungeonRuns.map((run) => (
                <div key={run.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: 12, border: '1px solid #F0F0F0', borderRadius: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13 }}>
                    <strong>{difficultyLabels[run.difficulty]}</strong> · {run.status === 'running' ? '진행 중' : run.status === 'completed' ? '보상 대기' : '수령 완료'}
                    <br />보상 {run.rewardPoints}P · {run.rewardExp}EXP
                  </div>
                  {run.status === 'completed' && <Button variant="primary" size="small" onClick={() => actions.claimDungeonReward(run.id)}>보상 받기</Button>}
                </div>
              ))}
              {dungeonRuns.length === 0 && <span style={{ fontSize: 13, color: '#666' }}>아직 모험 기록이 없어요.</span>}
            </div>
          </Card>
        )}

        {activeHubTab === 'friends' && (
          <Card padding="20px" borderRadius="16px" style={{ marginTop: 12 }}>
            <h2 style={sectionTitleStyle}>우리 반 캐릭터</h2>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <Button variant="primary" onClick={actions.shareCharacter}>내 캐릭터 공유하기</Button>
              <Button variant="outline" onClick={actions.refresh}>새로고침</Button>
            </div>
            <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
              {shares.map((share) => (
                <div key={share.id} style={{ padding: 14, border: '1px solid #F0F0F0', borderRadius: 12, background: '#fff' }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{share.petName}</div>
                  <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>{share.ownerName} · Lv.{share.level} · {ORIGINAL_PET_ASSETS[share.petForm]?.name || '별알'}</div>
                </div>
              ))}
              {shares.length === 0 && <span style={{ fontSize: 13, color: '#666' }}>아직 공유된 캐릭터가 없어요.</span>}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default VirtualPetHub;
